export interface GQLResponseData<T> {
  data: T | null;
  errors?: any[];
}

interface RawJsonError {
  message: string;
}

interface RawJson {
  errors?: RawJsonError[];
}

export const cycloneAuthenticationInvalidError = {
  message:
    "Your commenting session has expired or is no longer valid. Please refresh the page and try again.",
  extensions: {
    code: "CycloneAuthenticationInvalid",
  },
};

const hasInvalidTokenError = (errors: RawJsonError[] | undefined) =>
  errors?.some((error) => error.message === "Token is invalid.") ?? false;

export async function fetchWithAuth(
  url: string,
  props?: RequestInit,
  accessToken: string | null | undefined = null,
) {
  // by default, we set the content type to JSON
  // since most of our requests use json, but if you
  // pass in a different "Content-Type" in the `headers`
  // for the `props`, it will override this value
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  if (accessToken) {
    headers["Authorization"] = `Bearer ${accessToken}`;
  }

  const sentProps: RequestInit = {
    // by default for our API calls, we reload from
    // cache because Cyclone doesn't like stale data.
    //
    // You can override if you pass in a different value
    // via `props`
    cache: "reload",
    ...props,
    headers: {
      ...headers,
      ...props?.headers,
    },
  };

  return await fetch(url, sentProps);
}

export async function makeRequest<T>(
  url: string,
  query: string,
  variables: {
    [key: string]: string | Date | number | boolean | undefined | object | null;
  } = {},
  accessToken: string | null | undefined = null,
  signal?: AbortSignal,
): Promise<GQLResponseData<T>> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  const response = await fetchWithAuth(
    url,
    {
      cache: "reload",
      method: "POST",
      headers,
      signal,
      body: JSON.stringify({
        query,
        variables,
      }),
    },
    accessToken,
  );

  if (!response.ok && response.status === 403) {
    try {
      const json = (await response.json()) as RawJson;
      if (Array.isArray(json.errors) && hasInvalidTokenError(json.errors)) {
        return {
          data: null,
          errors: [cycloneAuthenticationInvalidError],
        };
      }
    } catch (e) {
      console.error(e);
      return {
        data: null,
      };
    }
  }

  if (!response.ok) {
    const statusText = response.statusText;
    const error = await response.text();
    console.error(statusText, error);

    return {
      data: null,
      errors: [error],
    };
  }

  const json = (await response.json()) as GQLResponseData<T>;
  if (Array.isArray(json.errors) && hasInvalidTokenError(json.errors)) {
    return {
      data: null,
      errors: [cycloneAuthenticationInvalidError],
    };
  }

  if (json.errors) {
    return {
      data: null,
      errors: json.errors,
    };
  }

  return {
    data: json.data,
  };
}

// Custom error class for handling errors from Cyclone GraphQL requests.
// Can add path/locations to this in the future if needed.
export class CycloneGraphQLRequestError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "CycloneGraphQLRequestError";
  }
}
